// Manuel de procédures — utilitaires : statuts, dates, texte, sommaire, diff.
// Repris du prototype Claude Design (procedures-lib.jsx), sans changement de
// logique.

export const PROC_STATUS = {
  publie: { lab: "Publié", tone: "ok" },
  brouillon: { lab: "Brouillon", tone: "neut" },
  revision: { lab: "À réviser", tone: "warn" },
};

export const PROC_EV = {
  create: { lab: "a créé", tone: "ok" },
  edit: { lab: "a modifié", tone: "info" },
  restore: { lab: "a restauré", tone: "warn" },
  status: { lab: "a changé le statut de", tone: "neut" },
  meta: { lab: "a mis à jour", tone: "neut" },
  delete: { lab: "a supprimé", tone: "bad" },
  undelete: { lab: "a restauré depuis la corbeille", tone: "warn" },
  section: { lab: "a créé la section", tone: "ok" },
  acl: { lab: "a modifié les accès de", tone: "neut" },
};

export const PROC_EV_FILTERS = [
  ["all", "Tout"], ["create", "Créations"], ["edit", "Modifications"], ["restore", "Restaurations"],
  ["status", "Statuts"], ["delete", "Suppressions"], ["acl", "Accès"],
];

export const PROC_SPACE_ICON = { compta: "calc", social: "people", juri: "scale", cabinet: "office", it: "monitor" };

export const procFmt = (ts, withTime = true) =>
  new Date(ts).toLocaleDateString("fr-FR", {
    day: "numeric", month: "short", year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });

export const procRel = (ts) => {
  const s = (Date.now() - ts) / 1000;
  if (s < 60) return "à l'instant";
  if (s < 3600) return "il y a " + Math.floor(s / 60) + " min";
  if (s < 86400) return "il y a " + Math.floor(s / 3600) + " h";
  const d = Math.floor(s / 86400);
  if (d === 1) return "hier";
  if (d < 30) return "il y a " + d + " j";
  if (d < 365) return "il y a " + Math.floor(d / 30) + " mois";
  return "il y a " + Math.floor(d / 365) + " an" + (d >= 730 ? "s" : "");
};

export const procText = (html) => {
  const el = document.createElement("div");
  el.innerHTML = html;
  return el.textContent.replace(/\s+/g, " ").trim();
};

export const procCur = (p) => p.versions[p.versions.length - 1];

export const procNorm = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export const procEsc = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/* ---------- Titres + sommaire ---------- */

export const procWithToc = (html, linkInfo) => {
  const el = document.createElement("div");
  el.innerHTML = html;
  const toc = [];
  if (linkInfo) {
    el.querySelectorAll("a.pr-xref").forEach((a) => {
      const t = linkInfo(a.dataset.page);
      if (!t) {
        a.classList.add("is-broken");
        a.title = "Procédure introuvable ou non accessible";
      } else a.title = t;
    });
  }
  el.querySelectorAll("h2, h3").forEach((h, i) => {
    const id = "pr-h-" + i;
    h.id = id;
    toc.push({ id, lvl: h.tagName === "H2" ? 2 : 3, t: h.textContent });
  });
  return { html: el.innerHTML, toc };
};

/* ---------- Diff ---------- */

const procBlocks = (html) => {
  const root = document.createElement("div");
  root.innerHTML = html;
  const out = [];
  [...root.children].forEach((n) => {
    const t = n.tagName.toLowerCase();
    if (t === "ul" || t === "ol") {
      [...n.children].forEach((li, i) =>
        out.push((t === "ol" ? i + 1 + ". " : n.classList.contains("check") ? "☐ " : "• ") + li.textContent.trim()));
    } else if (t === "table") {
      n.querySelectorAll("tr").forEach((tr) =>
        out.push("▦ " + [...tr.children].map((c) => c.textContent.trim()).join("  |  ")));
    } else if (t === "div" && n.classList.contains("callout")) out.push("▍ " + n.textContent.trim());
    else if (t === "h2") out.push("## " + n.textContent.trim());
    else if (t === "h3") out.push("### " + n.textContent.trim());
    else out.push(n.textContent.trim());
  });
  if (!root.children.length && root.textContent.trim()) out.push(root.textContent.trim());
  return out.filter(Boolean);
};

const procLcs = (a, b) => {
  const m = a.length;
  const n = b.length;
  const T = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      T[i][j] = a[i] === b[j] ? T[i + 1][j + 1] + 1 : Math.max(T[i + 1][j], T[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { ops.push({ op: "=", t: a[i] }); i++; j++; }
    else if (T[i + 1][j] >= T[i][j + 1]) ops.push({ op: "-", t: a[i++] });
    else ops.push({ op: "+", t: b[j++] });
  }
  while (i < m) ops.push({ op: "-", t: a[i++] });
  while (j < n) ops.push({ op: "+", t: b[j++] });
  return ops;
};

export const procDiff = (oldHtml, newHtml, oldTitle, newTitle) => {
  const a = ["# " + oldTitle, ...procBlocks(oldHtml)];
  const b = ["# " + newTitle, ...procBlocks(newHtml)];
  const ops = procLcs(a, b);
  const rows = [];
  for (let k = 0; k < ops.length; k++) {
    // paire suppression/ajout consécutive -> diff mot à mot
    if (ops[k].op === "-" && ops[k + 1] && ops[k + 1].op === "+") {
      const w = procLcs(ops[k].t.split(/(\s+)/), ops[k + 1].t.split(/(\s+)/));
      const same = w.filter((x) => x.op === "=" && x.t.trim()).length;
      const tot = w.filter((x) => x.t.trim()).length;
      if (same / Math.max(1, tot) > 0.4) { rows.push({ op: "~", words: w }); k++; continue; }
    }
    rows.push(ops[k]);
  }
  return {
    rows,
    add: rows.filter((r) => r.op === "+" || r.op === "~").length,
    del: rows.filter((r) => r.op === "-" || r.op === "~").length,
  };
};

/* ---------- Modèles de page ---------- */

// Rédaction par l'IA : réservée à ces quatre personnes, même quand le manuel
// sera ouvert plus largement (contrôle identique côté serveur).
const PROC_IA_EMAILS = [
  "nomena@alfa-partenaires.com",
  "jonathan@alfa-partenaires.com",
  "samuel.hoarau@alfacomptabilite.com",
  "anthony.hoarau@alfacomptabilite.com",
];
export const procIaAutorise = (u) => PROC_IA_EMAILS.includes((u?.email || "").toLowerCase());

export const PROC_TEMPLATES = [
  {
    id: "proc", name: "Procédure pas à pas", desc: "Objectif, prérequis, étapes, vigilance",
    html: `<h2>Objectif</h2><p>Décrire en une phrase le résultat attendu.</p><h2>Périmètre</h2><p>Dossiers, collaborateurs ou situations concernés.</p><h2>Prérequis</h2><ul><li>Document ou accès nécessaire</li></ul><h2>Étapes</h2><ol><li>Première étape</li><li>Deuxième étape</li></ol><div class="callout warn"><p><strong>Point de vigilance :</strong> erreur fréquente à éviter.</p></div><h2>Références</h2><p>Textes, modèles ou pages liées.</p>`,
  },
  {
    id: "check", name: "Liste de contrôle", desc: "Points à valider avant, pendant, après",
    html: `<h2>Avant</h2><ul class="check"><li>Point à vérifier</li></ul><h2>Pendant</h2><ul class="check"><li>Point à vérifier</li></ul><h2>Après</h2><ul class="check"><li>Point à vérifier</li></ul>`,
  },
  {
    id: "faq", name: "Questions fréquentes", desc: "Questions / réponses courtes",
    html: `<h3>Question ?</h3><p>Réponse.</p><h3>Question ?</h3><p>Réponse.</p>`,
  },
  { id: "blank", name: "Page vierge", desc: "Commencer de zéro", html: `<p></p>` },
];
